import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { FiMail, FiArrowLeft, FiCheckCircle } from 'react-icons/fi';
import { API_URL } from '../../api/client';
import { Button } from '../../components/Button';
import { ErrorMessage } from '../../components/ErrorMessage';

const ForgotPassword: React.FC = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await fetch(`${API_URL}/password-reset/request`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      });

      const data = await response.json();

      if (response.ok) {
        setSuccess(true);
      } else {
        setError(data.message || 'Failed to send reset link. Please try again.');
      }
    } catch (err) {
      setError('Network error. Please check your connection and try again.');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="auth-page">
        <div className="auth-container">
          <div className="auth-card">
            <div className="auth-header" style={{ textAlign: 'center' }}>
              <div style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '64px', height: '64px', backgroundColor: 'var(--color-success-light)', borderRadius: '50%', marginBottom: 'var(--space-4)' }}>
                <FiCheckCircle style={{ width: '32px', height: '32px', color: 'var(--color-success)' }} />
              </div>
              <h2>Check Your Email</h2>
              <p className="auth-subtitle" style={{ marginBottom: 'var(--space-4)' }}>
                If an account exists with <strong>{email}</strong>, we've sent a password reset link to that email address.
              </p>
              <p style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-tertiary)', marginBottom: 'var(--space-6)' }}>
                The link will expire in 1 hour. If you don't see the email, check your spam folder.
              </p>
            </div>
            <div className="auth-footer">
              <p className="auth-link-text">
                <Link to="/admin/login" className="auth-link">
                  <FiArrowLeft style={{ marginRight: 'var(--space-2)', display: 'inline' }} />
                  Back to Admin Login
                </Link>
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-page">
      <div className="auth-container">
        <div className="auth-card">
          <div className="auth-header">
            <h2>Forgot Password?</h2>
            <p className="auth-subtitle">
              Enter your admin email address and we'll send you a link to reset your password.
            </p>
          </div>

          {error && <ErrorMessage message={error} onClose={() => setError('')} />}

          <form onSubmit={handleSubmit} className="auth-form">
            <div className="form-group">
              <label htmlFor="email">Admin Email Address</label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="admin@coinmfi.com"
                required
                disabled={loading}
                autoComplete="email"
              />
            </div>

            <Button
              type="submit"
              variant="primary"
              size="lg"
              fullWidth
              loading={loading}
              disabled={loading}
            >
              Send Reset Link
            </Button>
          </form>

          <div className="auth-footer">
            <p className="auth-link-text">
              <Link to="/admin/login" className="auth-link">
                <FiArrowLeft style={{ marginRight: 'var(--space-2)', display: 'inline' }} />
                Back to Admin Login
              </Link>
            </p>
            <div className="auth-divider"></div>
            <p className="auth-link-text auth-link-secondary">
              Customer? <Link to="/login" className="auth-link">Customer Portal</Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
